// Home.js
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "../seo";
import "../style/home.css";

/* Every loop in src/img/GIF, picked at random once per page load.

   These are .mp4 now, not .gif. The source art is still gif — the files sit
   next to these — but GIF stores whole frames against a 256-colour palette
   with no real interframe compression, and the set ran to 42MB, one file
   alone at 15MB. The same eight as H.264 are 9.3MB, every one under 2MB,
   and at the size this frame actually displays them the two are hard to
   tell apart. Nothing was traded for that except resolution nobody could
   see: the banners were 1400px wide arriving in a ~443px box.

   require.context is a webpack feature rather than an ES import, and it is
   what makes this a folder instead of a list: it resolves the glob at build
   time, so dropping a new file into src/img/GIF puts it in the rotation with
   no code change here. A plain `import` can't do that — the specifier has to
   be a literal, which is the whole reason the old single deadsec import
   existed. Recursive, because that folder has grown subfolders and a
   non-recursive scan dropped everything inside them with no error to say so.

   Each entry comes back as a module, and whether that module is the URL
   string or an object wrapping it in .default depends on how the asset rule
   is configured, so unwrap both shapes. */
const loopContext = require.context("../img/GIF", true, /\.mp4$/);
const LOOPS = loopContext.keys().map(key => {
  const mod = loopContext(key);
  return mod && mod.default ? mod.default : mod;
});

/* Module scope, not component state: this file is imported once per page
   load, so the choice is made once per load and then held. Picking inside
   the component would reshuffle it on every re-render — and this component
   re-renders on each theme toggle, via the observer below. */
const LOOP = LOOPS.length ? LOOPS[Math.floor(Math.random() * LOOPS.length)] : null;

/* Autoplay is refused unless the element is really muted, and the attribute
   alone has historically not been enough — so it is set as a property too,
   where the browser actually reads it. The same callback honours a reduced
   motion preference by holding the first frame instead of looping, which is
   the one thing a video does that the gif could not be asked to stop doing. */
function setupLoop(el) {
  if (!el) return;
  el.muted = true;
  const still = window.matchMedia("(prefers-reduced-motion: reduce)");
  if (still.matches) {
    el.autoplay = false;
    el.pause();
  }
}

export default function Home() {
  usePageMeta({
    description:
      "Cyber-security research by Brutal Panda — Windows internals, malware " +
      "analysis and offensive technique write-ups with working code.",
    path: "/",
  });

  const navigate = useNavigate();
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [theme, setTheme] = useState("dark");

  useEffect(() => {
    // Detect current theme
    const currentTheme = document.documentElement.getAttribute("data-theme") || "dark";
    setTheme(currentTheme);

    // Listen for theme changes
    const observer = new MutationObserver(() => {
      const newTheme = document.documentElement.getAttribute("data-theme") || "dark";
      setTheme(newTheme);
    });

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetch("/blog/meta.json")
      .then(res => res.json())
      .then(data => {
        // ✅ Only include visible posts
        const visiblePosts = data.filter(post => post.visible === true);

        // ✅ Sort by ID (highest first - most recent)
        const sorted = visiblePosts.sort((a, b) => b.id - a.id);

        // ✅ Take top 3 (already filtered)
        setFeaturedPosts(sorted.slice(0, 3));
      })
      .catch(err => console.error("Failed to load blog posts", err));
  }, []);

  return (
    <section id="home" className="section active retro-section">
      {/* Welcome Note */}
      <div className="notes retro-block">
        {/* heading and intro are wrapped as one block so this column has the
            same two parts the RECENT BLOG column has — a heading band and a
            media band. home.css lines the two columns up on those bands. */}
        <div className="notes__intro">
          <h3>WELCOME TO MY CYBERSEC HUB</h3>
          <p>Everything here is part of my journey in Sec. Enjoy :)</p>
        </div>
        {/* Guarded, so an empty folder renders nothing rather than a broken
            element. All styling is in home.css — the frame has to hold one
            fixed size across clips of three different aspect ratios, which
            is not something inline styles can express. */}
        {LOOP && (
          <div className="gif-frame">
            {/* aria-hidden rather than a label: which clip you get is random,
                so there is nothing true to say about it, and it carries no
                meaning the surrounding text doesn't already. It is decoration,
                and it stays out of the accessibility tree. */}
            <video
              ref={setupLoop}
              className="gif-frame__media"
              src={LOOP}
              autoPlay
              muted
              loop
              playsInline
              aria-hidden="true"
            />
          </div>
        )}
      </div>

      {/* Featured Blogs */}
      <div className="featured retro-block">
        <h3>RECENT BLOG</h3>
        {featuredPosts.length === 0 ? (
          <p className="loading">Loading post...</p>
        ) : (
          (() => {
            // ✅ Now just take the first one (already sorted by date)
            const recentPost = featuredPosts[0];

            // Link with `url` (the route), not `path` — `path` locates the
            // markdown on disk ("syscall-hooking/index") and 404s as a route.

            return (
              <div
                className="featured-item retro-card hoverable"
                onClick={() => navigate(`/blog/${recentPost.url}`)}
              >
                <h4 className="featured-title">{recentPost.title}</h4>
                <p>{recentPost.description}</p>

                {recentPost.tags && recentPost.tags.length > 0 && (
                  <div className="tags">
                    {recentPost.tags.map((tag, i) => (
                      <span className="tag" key={i}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* Quote with background depth */}
      <div
        className="terms retro-block quote-bg"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/${theme === "light" ? "background.webp" : "ghost.webp"})`
        }}
      >
        <h3>$ fortune</h3>
        <p>
          "If we all reacted the same way, we’d be <u>predictable</u>.
          There’s always more than <i><u> one way to approach a challenge.</u></i>"
          <span className="author"> — Major Kusanagi</span>
        </p>
      </div>
    </section>
  );
}